import { useState, useEffect, useCallback } from "react"
import { createPortal } from "react-dom"
import PubSub from "pubsub-js"
import { api } from "aldoo-ra/API"
import { store } from "aldoo-ra/Store"
import { getPricingData } from "aldoo-ra/Store/api"
import Localization from "aldoo-ra/Localization"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Spinner from "aldoo-ra/Spinner"
import {
  XMarkIcon,
  ArrowDownTrayIcon,
  LockClosedIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline"
import { CheckIcon } from "@heroicons/react/24/solid"

const transferAPI = api("aldoo-transfer/pricing")

// PubSub topic used to open the dialog from anywhere in the app.
export const GO_PREMIUM_TOPIC = "open-go-premium"

/// Open the upgrade dialog. Pass an optional `reason` payload (e.g. the
/// "You've reached your plan's transfer limit." sentence) and the dialog
/// surfaces it above the marketing copy so the user sees WHY this dialog
/// is showing, not just a generic "Go Pro" pitch.
export function openGoPremium(reason) {
  PubSub.publish(GO_PREMIUM_TOPIC, reason || null)
}

// Replicates the pricing-page purchase: drop the Pro plan into the cart and
// jump straight to checkout, as if the user clicked "Buy" on the Pro card.
function buyPlan(plan, storeData, billing) {
  const productId = typeof plan.product === "string" ? plan.product : (plan.product?.item || plan.product?._id)
  const product = storeData.products?.[productId]
  const priceId = product?.price?._id || product?.price || product?.meta?.prices?.monthly
  const storePrice = storeData.prices?.[priceId]
  if (!storePrice?.price_model) return false

  const byInterval = (interval) => storePrice.price_model.find((m) => m.phases?.[0]?.interval === interval) ?? null
  const monthlyModel = byInterval("month")
  const annualModel = byInterval("year")
  const selectedModel = billing === "annual" ? (annualModel || monthlyModel) : (monthlyModel || annualModel)
  if (!selectedModel) return false

  const webEntry = selectedModel.real_product_id?.find((r) => {
    const platform = typeof r.platform === "object" ? r.platform?.value : r.platform
    return platform === "Web"
  })

  store.addToCart({
    product: {
      pid: product?.pid,
      product_id: storePrice._id,
      store_id: webEntry?.id || null,
      name: product?.name || plan.name,
      description: product?.description,
      currency: selectedModel.currency || "USD",
      price_model: [{ ...selectedModel, primary: true }],
      duration: billing === "annual" ? "/ year" : "/ month",
      thumbnail: product?.thumbnail,
    },
    proceedToCart: true,
  })
  return true
}

export default function GoPremiumDialog() {
  const { currentLanguage } = Localization()
  const texts = TextBundle({ id: "transfer_share" })
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [proPlan, setProPlan] = useState(null)
  const [features, setFeatures] = useState([])
  const [storeData, setStoreData] = useState({ products: {}, prices: {} })
  // Optional one-line reason surfaced above the marketing pitch — e.g.
  // "You've reached your plan's transfer limit." when openGoPremium was
  // called from the upload flow's quota-exceeded branch.
  const [reason, setReason] = useState(null)

  const load = useCallback(() => {
    if (!currentLanguage) return
    setLoading(true)
    Promise.all([getPricingData(currentLanguage), transferAPI({ pricing: true })])
      .then(([pricing, data]) => {
        const plans = pricing?.plans || []
        const pro = plans.find((p) => p.id === "pro") || plans.find((p) => p.is_popular)
        setProPlan(pro || null)
        setFeatures(pricing?.features || [])
        if (data?.products) setStoreData({ products: data.products, prices: data.prices || {} })
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [currentLanguage])

  useEffect(() => {
    const token = PubSub.subscribe(GO_PREMIUM_TOPIC, (_, payload) => {
      setReason(typeof payload === "string" ? payload : null)
      setOpen(true)
      if (!proPlan) load()
    })
    return () => PubSub.unsubscribe(token)
  }, [load, proPlan])

  if (!open) return null

  const close = () => setOpen(false)

  const handleGoUltimate = () => {
    if (!proPlan) return
    const ok = buyPlan(proPlan, storeData, "monthly")
    if (ok) close()
  }

  // Pull the Pro plan's actual feature values from the pricing matrix so the
  // dialog stays in sync with the live pricing page (no duplicated copy).
  const planId = (pv) => {
    const p = pv?.plan
    return typeof p === 'object' ? (p?._id || p?.id) : p
  }
  const proValue = (f) => {
    const proIds = [proPlan?._id, proPlan?.id].filter(Boolean)
    const v = f?.plan_values?.find((pv) => proIds.includes(planId(pv)))
    return v?.param ?? null
  }
  const isYes = (v) => {
    if (v == null) return false
    const s = String(v).trim().toLowerCase()
    return s === 'yes' || s === 'true' || s === '✓' || s === 'included'
  }
  const isNo = (v) => {
    if (v == null) return true
    const s = String(v).trim().toLowerCase()
    return s === 'no' || s === 'false' || s === '-' || s === '—' || s === '0'
  }
  // Feature list for the marketing pane: every feature the Pro plan actually
  // unlocks or improves. Hide features where Pro provides nothing.
  const proFeatures = (features || [])
    .map((f) => ({ name: f.name, value: proValue(f), tooltip: f.tooltip }))
    .filter((f) => f.name && !isNo(f.value))
    .slice(0, 8)

  // Local fallback if pricing hasn't loaded yet — keeps the dialog useful.
  const fallbackFeatures = [
    { name: texts?.unlimitedSize, value: null },
    { name: texts?.customBranding, value: null },
    { name: texts?.longerRetention, value: null },
  ]
  const leftFeatures = proFeatures.length ? proFeatures : fallbackFeatures

  // Portal to body so the dialog escapes any ancestor stacking context
  // (e.g. transforms/animations) — keeps it above the fixed top bar.
  if (typeof document === "undefined") return null
  return createPortal(
    <div className="fixed inset-0 z-[2147483647] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" />

      <div className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-3xl bg-paper shadow-2xl flex flex-col md:flex-row">
        {/* Left pane — feature matrix only */}
        <div className="relative hidden md:flex md:w-1/2 bg-gradient-to-br from-orange-300 via-orange-400 to-rose-400 p-8 flex-col gap-2 overflow-y-auto">
          <ul className="flex flex-col gap-2">
            {leftFeatures.map((f, i) => (
              <li
                key={i}
                className="flex items-center gap-3 rounded-xl bg-paper/95 border border-bordered/40 px-4 py-3 shadow-sm"
              >
                <span className="h-6 w-6 rounded-full bg-primary text-white flex items-center justify-center shrink-0">
                  <CheckIcon className="h-3.5 w-3.5" />
                </span>
                <span className="flex-1 text-sm font-medium text-text truncate">{f.name}</span>
                {f.value && !isYes(f.value) && (
                  <span className="text-xs font-semibold text-primary whitespace-nowrap">
                    {f.value}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Right pane — marketing copy + CTA */}
        <div className="relative w-full md:w-1/2 p-6 sm:p-10 overflow-y-auto flex flex-col">
          <button
            onClick={close}
            className="absolute top-4 right-4 p-1.5 rounded-lg text-text_secondary hover:bg-neutral_1"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>

          <div className="flex items-center gap-2 mb-4">
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1">
              <SparklesIcon className="h-3 w-3" />
              {texts?.pro}
            </span>
            <span className="text-text_secondary text-xs font-medium">
              {texts?.everythingYouGet}
            </span>
          </div>

          {/* Headline / subline. When the dialog was opened with a `reason`,
              that reason replaces the generic marketing headline so the user
              sees the specific limit they hit. The marketing pitch slides
              below as supporting copy.

              Split the reason at the first sentence boundary so the user
              gets one big headline (what they hit) and a smaller call-to-
              action subtitle (what to do about it) instead of a wall of
              h2 text that wraps to six lines.
          */}
          {reason ? (() => {
            const m = String(reason).match(/^(.*?[.!?])\s+(.+)$/s)
            const headline = m ? m[1] : reason
            const subtitle = m ? m[2] : null
            return (
              <>
                <Typography variant="h2" className="text-text mb-3 pr-8">
                  {headline}
                </Typography>
                {subtitle && (
                  <Typography variant="txt1_dim" className="block mb-4">
                    {subtitle}
                  </Typography>
                )}
                <Typography variant="txt2_dim" className="block mb-8">
                  {texts?.proPitch}
                </Typography>
              </>
            )
          })() : (
            <>
              <Typography variant="h2" className="text-text mb-3 pr-8">
                {texts?.unlockEverything}
              </Typography>
              <Typography variant="txt1_dim" className="block mb-8">
                {texts?.proPitch}
              </Typography>
            </>
          )}

          <div className="mt-auto">

          <Button
            variant="primary"
            className="w-full mb-3"
            onClick={handleGoUltimate}
            disabled={loading || !proPlan}
          >
            {loading ? <Spinner className="w-[18px] h-[18px]" /> : texts?.goPro}
          </Button>

          <Button variant="secondary" className="w-full" onClick={close}>
            {texts?.noThanks}
          </Button>

          <Typography variant="txt2_dim" className="block text-center mt-4">
            {texts?.cancelAnytime}
          </Typography>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
